/**
 * route-catalog (US-AI-0295-S01 · @UC9400) — DANH MỤC ROUTE là NGUỒN SỰ THẬT của mọi deep-link AI.
 *
 * 🔴 VÌ SAO TỒN TẠI — BUG PRODUCTION ĐÃ ĐO:
 * `flow-catalog.ts` để deep-link là **chuỗi route trần** viết tay. Đối chiếu với
 * `apps/mobile/lib/routing/app_router.dart`: **7/9 route KHÔNG TỒN TẠI**
 * (`/payments/pending`, `/payments`, `/votes`, `/groups/new`, `/expenses/create`,
 * `/collections/reminder`, `/ai/proposals`).
 * Mobile gọi `context.push(route)` thẳng, không validate ⇒ **user bấm nút là vào màn lỗi**.
 * Preview KHÔNG lộ ra vì nó chỉ `toast.success("Điều hướng: …")` — chưa bao giờ điều hướng thật.
 *
 * 🔴 HAI THỨ FILE NÀY SỬA TẬN GỐC:
 *  1. **Route là DỮ LIỆU có kiểm chứng**, không phải chuỗi tự do — `route-catalog.spec.ts` đọc THẲNG
 *     `app_router.dart` và đối chiếu từng mục. Thêm/sửa route sai ⇒ ĐỎ ngay, không đợi user bấm.
 *  2. **Deep-link mang NHÃN**. Trước đây mobile chỉ có chuỗi route nên phải bịa nhãn chung chung
 *     "Mở màn hình xử lý", và screen-reader đọc nguyên `"Mở màn hình /payments/pending"`.
 *
 * 🔴 US-AI-0310 — MỞ RỘNG THÀNH CATALOG DÙNG CHUNG (PO 24/07). Mỗi mục nay mang đủ thứ màn admin
 * config cần: `labelVi/En` (Text) · `contextVi` (Context) · `ios` · `android` · `web`. Route vẫn nằm
 * ở CODE và có guard — admin sửa được 3 URL ngoài, KHÔNG sửa được route (xem docblock `pathTemplate`).
 *
 * Ghi chú lịch sử: `ROUTE_CATALOG.web` từng để RỖNG có chủ đích vì web-app chưa có bảng route AI.
 * Nay mỗi mục tự mang URL web riêng nên nhánh đó không còn cần thiết; giữ khoá `web: {}` để 0 call
 * site nào vỡ, sẽ dọn ở task sau khi chắc không ai đọc.
 *
 * THUẦN (DP-3): không DB, không LLM, không I/O.
 */

/** Một route đích trong catalog. `pathTemplate` phải TRÙNG KHỚP route thật của client. */
export interface RouteSpec {
  /**
   * Path có placeholder, vd `/collections/:collectionId/board`.
   *
   * 🔴 US-AI-0310 — ĐÂY LÀ THỨ ĐIỀU HƯỚNG THẬT trong app, và **admin KHÔNG sửa được** (không nằm
   * trong patch schema ⇒ trạng thái "admin đổi route" không biểu diễn được ở biên API). Lý do: route
   * là tính ĐÚNG ĐẮN, có guard đối chiếu `app_router.dart`; cho gõ tự do là mở lại đúng lỗ 7-link-chết
   * mà US-AI-0295 vừa vá, lần này không guard nào chặn được vì route nhập lúc runtime.
   */
  readonly pathTemplate: string;
  /** Tên các tham số bắt buộc — phải khớp đúng `:x` trong `pathTemplate` (guard kiểm). */
  readonly params: readonly string[];
  readonly labelVi: string;
  readonly labelEn: string;
  /**
   * 🔴 US-AI-0310 — mô tả cho ADMIN: link này AI dùng trong ngữ cảnh nào. **KHÔNG hiện cho user.**
   * Viết bằng tiếng người, KHÔNG nhét path (màn config đã có cột route riêng).
   */
  readonly contextVi: string;
  /**
   * URL NGOÀI để mở app từ thông báo / chia sẻ. Admin sửa được 3 field này.
   *
   * ⚠️ SỰ THẬT HẠ TẦNG (đo từ repo): app dùng **App Links `https`, KHÔNG custom scheme**
   * (`AndroidManifest.xml:41-47` `autoVerify` + `Runner.entitlements:17-18` `applinks:`) ⇒ iOS và
   * Android mặc định CÙNG một URL, đó là cách App Links hoạt động chứ không phải sao chép cẩu thả.
   * 🔴 Android hiện chỉ khai `pathPrefix="/reset-password"` ⇒ URL cho path AI **chưa mở được app**
   * cho tới khi DevOps mở rộng manifest + file `apple-app-site-association`. Điền vào đây KHÔNG tự
   * làm link chạy — màn admin phải nói thẳng điều đó (nguyên tắc KHÔNG-ĐÁNH-LỪA).
   */
  readonly ios: string;
  readonly android: string;
  readonly web: string;
}

/**
 * Origin mặc định. Chỉ là GIÁ TRỊ KHỞI ĐIỂM trong code — mỗi môi trường (staging/prod) admin
 * override trong DB, vì host khác nhau (`goppy.app` vs `goppy-staging.madlab.tech`).
 */
const APP_LINK_ORIGIN = "https://goppy.app";
const WEB_ORIGIN = "https://web.goppy.app";

/** Dựng 3 URL ngoài từ route — một chỗ duy nhất, tránh 10 lần chép tay lệch nhau. */
function externalUrls(pathTemplate: string): Pick<RouteSpec, "ios" | "android" | "web"> {
  return {
    ios: `${APP_LINK_ORIGIN}${pathTemplate}`,
    android: `${APP_LINK_ORIGIN}${pathTemplate}`,
    web: `${WEB_ORIGIN}${pathTemplate}`,
  };
}

/** Deep-link đã giải xong: path ĐÃ điền tham số + nhãn đọc được. */
export interface DeepLink {
  readonly key: string;
  readonly path: string;
  readonly labelVi: string;
  readonly labelEn: string;
}

const MOBILE: Readonly<Record<string, RouteSpec>> = Object.freeze({
  // ── Onboarding ────────────────────────────────────────────────────────────────────────────────
  GROUP_CREATE: Object.freeze({
    pathTemplate: "/create-group",
    params: Object.freeze([]),
    labelVi: "Tạo nhóm đầu tiên",
    labelEn: "Create your first group",
    contextVi: "AI mời tạo nhóm khi người dùng chưa thuộc nhóm nào.",
    ...externalUrls("/create-group"),
  }),
  FUND_CREATE: Object.freeze({
    pathTemplate: "/funds/new",
    params: Object.freeze([]),
    labelVi: "Tạo quỹ",
    labelEn: "Create a fund",
    contextVi: "AI mời tạo quỹ khi nhóm đã có nhưng chưa có quỹ nào.",
    ...externalUrls("/funds/new"),
  }),
  FUND_LIST: Object.freeze({
    pathTemplate: "/funds",
    params: Object.freeze([]),
    labelVi: "Xem quỹ của tôi",
    labelEn: "View my funds",
    contextVi: "AI dẫn về danh sách quỹ khi câu hỏi chưa chốt được quỹ cụ thể.",
    ...externalUrls("/funds"),
  }),

  // ── Việc cần làm ─────────────────────────────────────────────────────────────────────────────
  /** Thay `/payments/pending` (KHÔNG tồn tại). Bảng đối chiếu của MỘT đợt thu. */
  COLLECTION_BOARD: Object.freeze({
    pathTemplate: "/collections/:collectionId/board",
    params: Object.freeze(["collectionId"]),
    labelVi: "Xác nhận tại đây",
    labelEn: "Confirm here",
    contextVi: "AI nhắc chủ nhóm còn khoản chuyển khoản đang chờ xác nhận.",
    ...externalUrls("/collections/:collectionId/board"),
  }),
  /** Thay `/payments` (KHÔNG tồn tại). Màn đóng tiền của MỘT đợt thu. */
  PAY_COLLECTION: Object.freeze({
    pathTemplate: "/pay/:collectionId",
    params: Object.freeze(["collectionId"]),
    labelVi: "Đóng ngay",
    labelEn: "Pay now",
    contextVi: "AI nhắc thành viên còn khoản chưa đóng của một đợt thu.",
    ...externalUrls("/pay/:collectionId"),
  }),
  /** Thay `/votes` (KHÔNG tồn tại). Màn bỏ phiếu của MỘT đề xuất. */
  VOTE_PROPOSAL: Object.freeze({
    pathTemplate: "/vote/:proposalId",
    params: Object.freeze(["proposalId"]),
    labelVi: "Bỏ phiếu",
    labelEn: "Vote",
    contextVi: "AI nhắc còn phiếu biểu quyết đang mở chờ người dùng bỏ phiếu.",
    ...externalUrls("/vote/:proposalId"),
  }),
  /** Thay `/collections/reminder` (KHÔNG tồn tại). Nhắc người chưa đóng của MỘT đợt thu. */
  COLLECTION_REMIND: Object.freeze({
    pathTemplate: "/collections/:collectionId/remind",
    params: Object.freeze(["collectionId"]),
    labelVi: "Gửi nhắc đóng quỹ",
    labelEn: "Send payment reminder",
    contextVi: "AI đề nghị gửi nhắc tới những người chưa đóng của một đợt thu.",
    ...externalUrls("/collections/:collectionId/remind"),
  }),
  /** Thay `/expenses/create` (KHÔNG tồn tại). Ghi chi thuộc MỘT quỹ. */
  EXPENSE_CREATE: Object.freeze({
    pathTemplate: "/funds/:fundId/create-expense",
    params: Object.freeze(["fundId"]),
    labelVi: "Ghi khoản chi",
    labelEn: "Record an expense",
    contextVi: "AI dẫn tới màn ghi khoản chi khi usecase ghi chi qua trò chuyện bị tắt.",
    ...externalUrls("/funds/:fundId/create-expense"),
  }),
  /** Mở đợt thu thuộc MỘT quỹ. */
  COLLECTION_CREATE: Object.freeze({
    pathTemplate: "/funds/:fundId/create-collection",
    params: Object.freeze(["fundId"]),
    labelVi: "Mở đợt thu",
    labelEn: "Open a collection",
    contextVi: "AI dẫn tới màn mở đợt thu khi usecase mở đợt thu qua trò chuyện bị tắt.",
    ...externalUrls("/funds/:fundId/create-collection"),
  }),
  /**
   * 🔴 US-AI-0310 — THAY chuỗi chết `/reports/export` (`capabilities.ts` + `query-intent.ts`).
   * Route thật trong `app_router.dart` là `/funds/:fundId/export-report`.
   */
  REPORT_EXPORT: Object.freeze({
    pathTemplate: "/funds/:fundId/export-report",
    params: Object.freeze(["fundId"]),
    labelVi: "Xuất báo cáo",
    labelEn: "Export report",
    contextVi: "AI nói rõ chưa xuất file được và dẫn người dùng tới màn xuất báo cáo của quỹ.",
    ...externalUrls("/funds/:fundId/export-report"),
  }),
  /**
   * 🔴 US-E23.F7-0337 (@UC9550 @UC9551 @UC9552) — CỬA GUI của họ KHỞI TẠO, trỏ **màn có thật trong bộ
   * khung mobile MỚI** (`351b3396` / PR #324): `HomeRoutePaths.root`.
   *
   * Bản nháp đầu của task này khai `GROUP_MEMBERS → /group/:id/members` theo app CŨ. App cũ đã bị thay:
   * bộ khung mới chỉ có `/home`, `/login`, `/register`. Thêm một key trỏ màn không tồn tại là **tự tay
   * đẻ thêm link chết thứ 12** — đúng thứ US-AI-0310 vừa dọn (16/18 usecase từng dẫn tới màn lỗi).
   *
   * Chọn "chung mà SỐNG" thay vì "cụ thể mà CHẾT" là đúng tiền lệ của chính file này (`FUND_LIST` được
   * chọn làm fallback vì "không tham số, luôn giải được, luôn tồn tại").
   *
   * ⚠️ 8 key cũ (`GROUP_CREATE`, `FUND_CREATE`, `COLLECTION_*`, `EXPENSE_CREATE`, …) VẪN trỏ màn mà bộ
   * khung mới chưa dựng lại — việc căn lại toàn bộ thuộc task riêng (đã báo cáo), KHÔNG kéo vào 0337.
   */
  /**
   * 🔴 US-E23.F8 SỬA `/home` → `/`: 0337 khai `/home` theo bộ khung `apps/mobile` MỚI, nhưng client
   * ĐANG SHIP (`apps/flutter-web` — Dockerfile.web → image `web` → staging) khai màn chính là `/`.
   * ⇒ `APP_HOME` khi đó là **link CHẾT trên chính app người dùng đang dùng**, đúng thứ guard này sinh
   * ra để chặn. Guard không kêu được vì nó đang đọc một đường dẫn đã biến mất (ENOENT, CI #118).
   *
   * Ngày `apps/mobile` lên thay, `/` sẽ cần đổi thành `/home` — việc đó đã được đếm trong
   * `mobile-rebuild-debt.spec.ts` nên không rơi vào quên lãng.
   */
  APP_HOME: Object.freeze({
    pathTemplate: "/",
    params: Object.freeze([]),
    labelVi: "Mở ứng dụng",
    labelEn: "Open the app",
    contextVi: "AI dẫn về màn chính khi usecase khởi tạo qua trò chuyện bị tắt.",
    ...externalUrls("/"),
  }),
});

/**
 * 🔴 ALLOWLIST ĐÓNG. Mọi `deepLinkKey` phải ∈ đây.
 * `web` rỗng có chủ đích — xem docblock đầu file.
 */
export const ROUTE_CATALOG = Object.freeze({
  mobile: MOBILE,
  web: Object.freeze({} as Record<string, RouteSpec>),
});

/**
 * 🔴🔴 US-AI-0438-S03 (@UC9687) — KHOÁ DẪN TỚI MÀN **GHI TIỀN / NHẮC NỢ**.
 *
 * Quỹ `frozen` chặn mọi WRITE bằng 409 `FUND_FROZEN`; nhóm `suspended` bị `FundAccessGuard` freeze
 * nghiệp vụ. Dựng nút ghi trong hai trạng thái đó = **ngõ cụt do chính lời chào tạo ra** (DP-7):
 * người dùng bấm, điền hết form, rồi nhận 409 — đúng lúc nhóm đang ở tình trạng nhạy cảm nhất.
 *
 * ⚠️ **CHỈ khoá GHI.** Freeze chặn WRITE, **không** chặn READ (`schema.prisma` ghi rõ) — `FUND_LIST`,
 * `COLLECTION_BOARD` vẫn phải hiện. Nới danh sách này thành "ẩn hết" là sửa quá tay theo hướng khác.
 *
 * `locked-scope.spec.ts` canh HAI CHIỀU bằng cách **tự khám phá** `ROUTE_CATALOG`: mọi khoá có
 * `pathTemplate` dạng ghi (`/pay/`, `/new`, `remind`) phải nằm trong đây, và mọi phần tử ở đây phải là
 * khoá có thật trong catalog. Thêm route ghi mới mà quên khai ⇒ ĐỎ.
 */
export const WRITE_DEEPLINK_KEYS = [
  "PAY_COLLECTION",
  "EXPENSE_CREATE",
  "COLLECTION_CREATE",
  "COLLECTION_REMIND",
  /**
   * 🔴 THÊM SO VỚI DANH SÁCH TAY TRONG SPEC — do chính test tự-khám-phá bắt được.
   * `/funds/new` là đường GHI: nhóm `suspended` freeze nghiệp vụ ⇒ tạo quỹ sẽ bị chặn. Spec liệt kê 4
   * khoá; phép đo tìm ra khoá thứ 5. Đây đúng lý do lưới phải tự khám phá thay vì chép danh sách.
   *
   * ⚠️ `GROUP_CREATE` (`/create-group`) CỐ Ý KHÔNG nằm đây: nó tạo một nhóm MỚI, không thuộc phạm vi
   * nhóm đang bị đình chỉ. Chặn nó là chặn oan — người bị đình chỉ một nhóm vẫn được lập nhóm của mình.
   */
  "FUND_CREATE",
] as const;

/**
 * Giải một `key` + tham số → `DeepLink`.
 *
 * 🔴 THIẾU THAM SỐ ⇒ `null` (BỎ nút). Cùng tinh thần DP-1 *"thà hỏi còn hơn ghi sai"*: thà KHÔNG có
 * nút, còn hơn một nút đưa người dùng vào màn lỗi — đó chính là bug đang vá.
 * Key ngoài catalog ⇒ `null` (allowlist đóng, không bịa route).
 */
export function resolveRoute(
  key: string,
  params: Readonly<Record<string, string | null | undefined>>,
): DeepLink | null {
  const spec = ROUTE_CATALOG.mobile[key];
  if (!spec) return null;

  let path = spec.pathTemplate;
  for (const name of spec.params) {
    const value = params[name];
    if (typeof value !== "string" || value.length === 0) return null;
    path = path.replace(`:${name}`, value);
  }
  return Object.freeze({ key, path, labelVi: spec.labelVi, labelEn: spec.labelEn });
}
